import {
    Box,
    Button,
    Card,
    CardBody,
    Modal,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    Avatar,
    VStack,
    Heading,
    ModalCloseButton,
    Stack,
    useRadioGroup,
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle
} from '@chakra-ui/react'
import { useAlertService } from '@/shared/lib/useAlertService'
import { useState } from 'react'
import {
    useChildByQrCode,
    useCheckinChild,
    useCheckoutChild
} from '@/modules/teacher/hooks/useCheckInOut'
import { useChildCheckinHistory } from '@/modules/teacher/hooks/useChildCheckinHistory'
import { getFromLocalStorage } from '@/shared/utils/localStorageHelper'
import { BranchType } from '@/shared/api/hooks/useBranches'
import UserRadioCard from './UserRadioCard'
import CheckinHistoryList from '@/modules/teacher/components/CheckinHistoryList'
import { CHECKIN_HISTORY_LIMIT } from '../constants'
import { CustomModalProps, User } from '../types'

const CheckInModal = ({ qrCode, onClose, isOpen, session }: CustomModalProps) => {
    const alertService = useAlertService()

    const { loading, data } = useChildByQrCode(qrCode, {
        onError: (error) => {
            alertService.error(error.message, error.name)
            onClose()
        }
    })

    // The child's own history. `openCheckin` is a previous session that was
    // never checked out — the backend refuses a new check-in while one exists,
    // so it has to be closed first.
    const {
        history,
        openCheckin,
        loading: historyLoading,
        refetch: refetchHistory
    } = useChildCheckinHistory(qrCode, CHECKIN_HISTORY_LIMIT)

    const [checkedById, setCheckedById] = useState('')
    // Kept separate from `checkedById` so the person who collected the child
    // last time can't leak into who is bringing them now.
    const [collectedById, setCollectedById] = useState('')
    const [code, setCode] = useState('')

    const [checkIn, { loading: checkinLoading }] = useCheckinChild({
        onCompleted: (result) => {
            setCode(result.checkin.checkinCode)
        }
    })
    const [checkOut, { loading: checkoutLoading }] = useCheckoutChild()
    const currentBranch = getFromLocalStorage<BranchType>('currentBranch')

    const childName = data?.getChildByQRCode
        ? `${data.getChildByQRCode.firstName} ${data.getChildByQRCode.lastName}`
        : ''
    const isResolving = !!openCheckin
    // Older records can lack a code. The backend compares it verbatim, so an
    // empty string would just be rejected — better to say so than to fail.
    const canCloseOpenCheckin = !!openCheckin?.checkinCode

    // Derived rather than mirrored into state: it is a pure function of `data`,
    // so an effect would only add a render pass.
    const groupData: User[] = data
        ? [
              ...data.getChildByQRCode.parents,
              ...(data.getChildByQRCode.alternativeContacts ?? [])
          ]
        : []

    const requireBranch = () => {
        if (!currentBranch) {
            alertService.error("Teacher's current branch is not set")
            return false
        }
        return true
    }

    /** Close the stale session, then drop straight back into the check-in flow. */
    const submitRemedialCheckout = async () => {
        if (!openCheckin || !currentBranch || !requireBranch()) return

        try {
            await checkOut({
                variables: {
                    input: {
                        qrCode,
                        checkOutBy: collectedById,
                        branch: currentBranch.value,
                        // Taken from the open record, so the teacher never has
                        // to know the code from a previous service.
                        checkinCode: openCheckin.checkinCode ?? '',
                        session: openCheckin.session ?? session
                    }
                }
            })
            alertService.success(
                `Closed the previous session for ${childName}. You can now check them in.`,
                'Previous check-in closed'
            )
            setCollectedById('')
            await refetchHistory()
        } catch (error: any) {
            alertService.error(error.message, error.name)
        }
    }

    const submitChildCheck = async () => {
        if (!currentBranch || !requireBranch()) return

        try {
            await checkIn({
                variables: {
                    input: {
                        qrCode,
                        checkInBy: checkedById,
                        branch: currentBranch.value,
                        session
                    }
                }
            })

            alertService.success(`Checked in ${childName}`, 'Success')
            setTimeout(onClose, 7500)
        } catch (error: any) {
            alertService.error(error.message, error.name)
        }
    }

    // Radio group for the person bringing the child in.
    const { getRootProps, getRadioProps } = useRadioGroup({
        name: 'checkedById',
        defaultValue: '',
        onChange: setCheckedById
    })

    // Separate group for who collected the child in the unclosed session.
    const { getRootProps: getCollectedRootProps, getRadioProps: getCollectedRadioProps } =
        useRadioGroup({
            name: 'collectedById',
            value: collectedById,
            onChange: setCollectedById
        })

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            motionPreset="slideInBottom"
            scrollBehavior="outside"
            blockScrollOnMount={false}
            size={{ base: 'xs', md: 'md' }}
        >
            <ModalOverlay backdropFilter="blur(8px)" />

            {loading ? (
                <Spinner size="lg" />
            ) : (
                <ModalContent bg="white">
                    <ModalHeader fontWeight="normal">Check-in process</ModalHeader>
                    <ModalCloseButton />

                    {code.length > 1 ? (
                        <Alert
                            status="success"
                            variant="subtle"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            textAlign="center"
                            height="150px"
                        >
                            <AlertIcon boxSize="30px" mr={0} />
                            <AlertTitle mt={4} mb={1} fontSize="lg">
                                Completed!
                            </AlertTitle>
                            <AlertDescription maxWidth="sm">
                                Child&apos;s checkin code is:&nbsp;
                                <b>{code}</b>
                            </AlertDescription>
                        </Alert>
                    ) : null}

                    {!!data && (
                        <Card>
                            <CardBody>
                                <VStack my={2} gap="4">
                                    <Avatar
                                        borderWidth="1px"
                                        size="2xl"
                                        borderColor="gray.100"
                                        name={childName}
                                        src={data.getChildByQRCode.picture}
                                    />
                                    <Text>Welcome, {childName}</Text>
                                </VStack>

                                {isResolving ? (
                                    <Box>
                                        <Alert
                                            status="warning"
                                            borderRadius="md"
                                            fontSize="sm"
                                        >
                                            <AlertIcon />
                                            <Box>
                                                <AlertTitle fontSize="sm">
                                                    Still checked in
                                                </AlertTitle>
                                                <AlertDescription>
                                                    {childName} was checked in
                                                    {openCheckin?.session
                                                        ? ` for ${openCheckin.session}`
                                                        : ''}{' '}
                                                    and never checked out. Close that
                                                    session first, then you can check them
                                                    in again.
                                                </AlertDescription>
                                            </Box>
                                        </Alert>

                                        <CheckinHistoryList
                                            history={history}
                                            loading={historyLoading}
                                        />

                                        <Heading py={4} size="md">
                                            Who collected this child?
                                        </Heading>
                                        <Stack
                                            marginTop={2}
                                            marginBottom={2}
                                            {...getCollectedRootProps()}
                                        >
                                            {groupData.map((parent) => (
                                                <UserRadioCard
                                                    key={parent._id}
                                                    parent={parent}
                                                    {...getCollectedRadioProps({
                                                        value: parent._id
                                                    })}
                                                />
                                            ))}
                                        </Stack>

                                        <Box pt={6}>
                                            {canCloseOpenCheckin ? null : (
                                                <Text
                                                    fontSize="sm"
                                                    color="red.500"
                                                    mb={2}
                                                >
                                                    That session has no check-in code on
                                                    record, so it can&apos;t be closed
                                                    here. Use the Queue page to check this
                                                    child out.
                                                </Text>
                                            )}
                                            <Button
                                                width="full"
                                                colorScheme="orange"
                                                isDisabled={
                                                    !collectedById || !canCloseOpenCheckin
                                                }
                                                isLoading={checkoutLoading}
                                                onClick={submitRemedialCheckout}
                                            >
                                                Check out previous session
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Heading py={4} size="md">
                                            Who is bringing this child?
                                        </Heading>
                                        <Stack
                                            marginTop={2}
                                            marginBottom={2}
                                            {...getRootProps()}
                                        >
                                            {groupData.map((parent) => (
                                                <UserRadioCard
                                                    key={parent._id}
                                                    parent={parent}
                                                    {...getRadioProps({
                                                        value: parent._id
                                                    })}
                                                />
                                            ))}
                                        </Stack>

                                        <CheckinHistoryList
                                            history={history}
                                            loading={historyLoading}
                                        />

                                        <Box pt={6}>
                                            <Button
                                                width="full"
                                                isDisabled={
                                                    checkedById === '' || code.length > 1
                                                }
                                                isLoading={checkinLoading}
                                                onClick={submitChildCheck}
                                            >
                                                Check in child
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </CardBody>
                        </Card>
                    )}
                </ModalContent>
            )}
        </Modal>
    )
}

export default CheckInModal
